import { PortfolioRoles } from "../api/generated/contracts";

/**
 * Single source of truth for which roles may reach which screen - both the AppLayout nav item
 * (tab visibility) and the App.tsx RequireRole wrapper (route gating) read from the SAME constant
 * here, so a tab and its route can never drift out of lockstep again (the bug this file fixes:
 * every tab but Import/Admin used to list ALL_ROLES while Audit's route only let a Viewer in far
 * enough to get redirected to /access-denied).
 *
 * This is navigation UX only, same as RequireRole's own header says: the authoritative,
 * resource-aware decision is always the server's IAuthorizationEngine call, never this list.
 *
 * Owner ruling (2026-10-02): "Viewer should only see Roadmap and Projects." Per that ruling,
 * Viewers lose the Products, Data quality, Decks, Archive, Import, Audit and Admin tabs/routes
 * entirely - including Decks/Archive, even though BRD section 4 grants Viewers "View published
 * (historical) decks". The owner's ruling narrows that for the nav/route UX only; the API's own
 * version.view grant to Viewer is UNCHANGED by this file - a Viewer who already has a direct link
 * or deep-link to a decks/archive resource still gets whatever the server decides, this file only
 * stops the in-app tab/route from offering that path.
 *
 * Audit here is PM_AND_ABOVE, which now matches the API's audit.view grant (BRD section 4,
 * IM-4's BrdSection4: Project Manager and Admin only) - previously Audit was ALL_ROLES in the nav
 * and route, so a Viewer or Contributor could click the tab only to be redirected by RequireRole.
 */
export const VIEWER_AND_ABOVE: readonly string[] = [
  PortfolioRoles.Viewer,
  PortfolioRoles.Contributor,
  PortfolioRoles.ProjectManager,
  PortfolioRoles.Admin,
];

export const CONTRIBUTOR_AND_ABOVE: readonly string[] = [
  PortfolioRoles.Contributor,
  PortfolioRoles.ProjectManager,
  PortfolioRoles.Admin,
];

export const PM_AND_ABOVE: readonly string[] = [PortfolioRoles.ProjectManager, PortfolioRoles.Admin];

export const ADMIN_ONLY: readonly string[] = [PortfolioRoles.Admin];

/** Per-screen role sets, keyed by route path. AppLayout's NAV_ITEMS and App.tsx's RequireRole
 * wrappers both read these - never redeclare a role list for a screen in either file. */
export const ROADMAP_ROLES = VIEWER_AND_ABOVE;
export const PROJECTS_ROLES = VIEWER_AND_ABOVE;
export const PROJECTS_NEW_ROLES = CONTRIBUTOR_AND_ABOVE;
export const PRODUCTS_ROLES = CONTRIBUTOR_AND_ABOVE;
export const DATA_QUALITY_ROLES = CONTRIBUTOR_AND_ABOVE;
export const DECKS_ROLES = CONTRIBUTOR_AND_ABOVE;
export const ARCHIVE_ROLES = CONTRIBUTOR_AND_ABOVE;
export const IMPORT_ROLES = CONTRIBUTOR_AND_ABOVE;
export const AUDIT_ROLES = PM_AND_ABOVE;
export const ADMIN_ROLES = ADMIN_ONLY;
